/**
 * Realtime Socket.IO client – namespace /realtime.
 *
 * Singleton socket that survives token refreshes. All hooks share the same
 * instance so event listeners are never orphaned.
 *
 * Backend events use ack callbacks:
 *   Client → Server: ping, join-tenant, leave-tenant, subscribe-project,
 *     unsubscribe-project, join-chat, leave-chat, typing:start, typing:stop,
 *     presence:query
 *   Server → Client: typing:start, typing:stop, presence:joined, presence:left,
 *     message:new, message:edited, message:deleted, reaction:added, reaction:removed
 */
import { io } from 'socket.io-client';
import { ENV } from '@configs/env';
import { useAuthStore } from '@stores/auth.store';
import { refreshAuthSingleFlight, hardSessionExpiredHardReset } from '@utils/networkSession';

const NAMESPACE = '/realtime';

function getBaseUrl() {
    try {
        return new URL(ENV.API_URL).origin;
    } catch {
        return 'http://localhost:4009';
    }
}

function getTenantId() {
    const user = useAuthStore.getState().user;
    return user?.tenantId ?? user?.tenant_id ?? null;
}

let socket = null;

// Project room subscription refcounting:
// Multiple screens/hooks (Project Discussion, Bug discussions, Blocker discussions)
// can be mounted at the same time for the same project. We should only emit
// `subscribe-project` once, and only emit `unsubscribe-project` when the last
// consumer releases the subscription.
const projectRoomRefCounts = new Map(); // projectId(string) -> number

function emitSubscribeProject(projectId) {
    if (!socket?.connected || !projectId) return;
    socket.emit('subscribe-project', { projectId: String(projectId) }, (res) => {
        if (!res?.success) console.warn('[realtime] subscribe-project:', res?.error ?? res);
    });
}

function joinTenantRoom(s) {
    const tenantId = getTenantId();
    if (tenantId && s?.connected) {
        s.emit('join-tenant', { tenantId }, (res) => {
            if (!res?.success) console.warn('[realtime] join-tenant:', res?.error ?? res);
        });
    }
}

/**
 * Get or create the singleton socket. Creates only once; subsequent calls
 * always return the same instance. Socket.IO handles reconnection internally.
 */
export function getRealtimeSocket() {
    const token = useAuthStore.getState().accessToken;
    if (!token) return null;

    if (socket) return socket;

    const url = `${getBaseUrl()}${NAMESPACE}`;
    socket = io(url, {
        auth: { token },
        extraHeaders: { Authorization: `Bearer ${token}` },
        // Keep realtime on a dedicated websocket channel.
        // Polling reconnect bursts can consume browser request slots and make API calls look stuck in "pending/queued".
        transports: ['websocket'],
        upgrade: false,
        timeout: 20000,
        reconnection: true,
        reconnectionAttempts: Infinity,
        reconnectionDelay: 1000,
        reconnectionDelayMax: 5000,
    });

    socket.on('connect', () => {
        joinTenantRoom(socket);
        // Re-subscribe all retained project rooms after reconnect.
        // Rooms are server-side connection state and must be re-joined on each connect.
        projectRoomRefCounts.forEach((count, projectId) => {
            if (count > 0) emitSubscribeProject(projectId);
        });
    });

    socket.on('connect_error', (err) => {
        const msg = err?.message ?? '';
        const looksAuth =
            msg.includes('AUTH_REQUIRED') ||
            msg.includes('AUTH_INVALID') ||
            msg.toLowerCase().includes('unauthorized') ||
            msg.toLowerCase().includes('jwt') ||
            msg.toLowerCase().includes('token');

        if (!looksAuth) return;

        // If the socket auth becomes invalid after idle, refresh the token and reconnect.
        refreshAuthSingleFlight()
            .then((tokens) => {
                if (!tokens?.accessToken) {
                    hardSessionExpiredHardReset();
                    return;
                }
                reconnectWithNewToken();
            })
            .catch((error) => {
                const status = error?.status ?? error?.response?.status;
                const isAuth = status === 400 || status === 401 || status === 403;
                if (isAuth) {
                    hardSessionExpiredHardReset();
                } else {
                    console.warn('Socket refresh failed due to network/server error. Reconnect scheduled.', error?.message);
                }
            });
    });

    return socket;
}

export function getChatSocket() {
    return getRealtimeSocket();
}

/**
 * Update the auth token on the existing socket and reconnect.
 * Preserves the socket instance so all hooks keep their listeners.
 * Skipped if the socket is in the middle of a connection attempt to avoid
 * the "WebSocket is closed before connection established" browser warning.
 */
export function reconnectWithNewToken() {
    const token = useAuthStore.getState().accessToken;
    if (!token || !socket) return;
    socket.auth = { token };
    socket.io.opts.extraHeaders = { Authorization: `Bearer ${token}` };
    // Only interrupt with disconnect().connect() when fully established.
    // If mid-handshake (active but !connected), the credentials above will
    // be picked up by the next reconnect attempt automatically.
    if (socket.connected) {
        socket.disconnect().connect();
    }
}

export function disconnectRealtimeSocket() {
    if (socket) {
        socket.disconnect();
        socket = null;
    }
    projectRoomRefCounts.clear();
}

export function disconnectChatSocket() {
    disconnectRealtimeSocket();
}

// --- Tenant ---

export function leaveTenant(tenantId) {
    if (!socket?.connected || !tenantId) return;
    socket.emit('leave-tenant', { tenantId }, (res) => {
        if (!res?.success) console.warn('[realtime] leave-tenant:', res?.error ?? res);
    });
}

// --- Project ---

export function subscribeProject(projectId) {
    if (!projectId) return;
    const key = String(projectId);
    const prev = projectRoomRefCounts.get(key) ?? 0;
    projectRoomRefCounts.set(key, prev + 1);

    // Emit only on first subscriber, and only when connected.
    if (prev > 0) return;
    emitSubscribeProject(key);
}

export function unsubscribeProject(projectId) {
    if (!projectId) return;
    const key = String(projectId);
    const prev = projectRoomRefCounts.get(key) ?? 0;
    const next = Math.max(0, prev - 1);
    if (next === 0) projectRoomRefCounts.delete(key);
    else projectRoomRefCounts.set(key, next);

    // Emit only when the last subscriber releases, and only when connected.
    if (!socket?.connected || prev <= 0 || next > 0) return;
    socket.emit('unsubscribe-project', { projectId: key }, (res) => {
        if (!res?.success) console.warn('[realtime] unsubscribe-project:', res?.error ?? res);
    });
}

// --- Ping ---

export function ping(callback) {
    if (!socket?.connected) {
        callback?.({ pong: 0, userId: '' });
        return;
    }
    socket.emit('ping', {}, (res) => callback?.(res ?? { pong: 0, userId: '' }));
}

// --- Chat ---

export function joinChat(chatId, callback) {
    if (!socket?.connected || !chatId) return;
    socket.emit('join-chat', { chatId }, (res) => {
        if (!res?.success) console.warn('[realtime] join-chat:', res?.error ?? res);
        callback?.(res);
    });
}

export function leaveChat(chatId, callback) {
    if (!socket?.connected || !chatId) return;
    socket.emit('leave-chat', { chatId }, (res) => {
        if (!res?.success) console.warn('[realtime] leave-chat:', res?.error ?? res);
        callback?.(res);
    });
}

// --- Typing (exactly one of chatId or projectId) ---

export function emitTypingStart(chatId) {
    if (!socket?.connected || !chatId) return;
    if (typeof import.meta !== 'undefined' && import.meta.env?.DEV) {
        // eslint-disable-next-line no-console
        console.debug('[realtime] emit typing:start', { chatId });
    }
    socket.emit('typing:start', { chatId }, (res) => {
        if (res && !res.success) console.warn('[realtime] typing:start:', res.error);
        if (typeof import.meta !== 'undefined' && import.meta.env?.DEV) {
            // eslint-disable-next-line no-console
            console.debug('[realtime] typing:start ack', res);
        }
    });
}

export function emitTypingStop(chatId) {
    if (!socket?.connected || !chatId) return;
    if (typeof import.meta !== 'undefined' && import.meta.env?.DEV) {
        // eslint-disable-next-line no-console
        console.debug('[realtime] emit typing:stop', { chatId });
    }
    socket.emit('typing:stop', { chatId }, (res) => {
        if (res && !res.success) console.warn('[realtime] typing:stop:', res.error);
        if (typeof import.meta !== 'undefined' && import.meta.env?.DEV) {
            // eslint-disable-next-line no-console
            console.debug('[realtime] typing:stop ack', res);
        }
    });
}

// --- Presence ---

export function queryPresence({ tenantId, projectId, chatId } = {}, callback) {
    if (!socket?.connected) {
        callback?.({ success: false, error: 'NOT_CONNECTED' });
        return;
    }
    socket.emit('presence:query', { tenantId, projectId, chatId }, (res) => {
        callback?.(res);
    });
}
